import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Activity, Building2, FileText, ShieldCheck } from "lucide-react";
import DarkVeil from "./DarkVeil";
import ElectricBorder from "./ElectricBorder";
import { primaryCtaClass } from "../utils/ctaClasses";
import { fadeUp, stagger } from "../utils/animations";

function CounterValue({
  value,
  duration = 1400,
  decimals = 0,
  suffix = "",
  useGrouping = false,
  reduceMotion = false,
}) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (reduceMotion) {
      setDisplayValue(value);
      return undefined;
    }

    let rafId;
    let startTime = null;

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = 1 - (1 - progress) ** 3;
      setDisplayValue(value * easedProgress);

      if (progress < 1) rafId = requestAnimationFrame(animate);
    };

    rafId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafId);
  }, [duration, reduceMotion, value]);

  const formatter = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping,
  });

  return `${formatter.format(displayValue)}${suffix}`;
}

export default function Hero() {
  const rightPanelRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: rightPanelRef,
    offset: ["start end", "end start"],
  });
  const parallaxY = useTransform(scrollYProgress, [0, 1], [20, -20]);
  const parallaxRotate = useTransform(scrollYProgress, [0, 1], [-1.5, 1.5]);

  return (
    <section
      id="home"
      className="relative isolate flex min-h-[100svh] scroll-mt-24 items-center overflow-hidden bg-slate-950 py-12 md:min-h-dvh md:py-20"
    >
      <p className="sr-only">
        Illustration: a hospital linked to dashboards for patients, appointments,
        revenue, bed management, and analytics.
      </p>
      <div aria-hidden className="pointer-events-none absolute inset-0 z-0 min-h-full [&_.darkveil-canvas]:min-h-full">
        <DarkVeil
          className="h-full min-h-full w-full"
          hueShift={210}
          noiseIntensity={0.02}
          scanlineIntensity={0.63}
          speed={2.5}
          scanlineFrequency={2.3}
          warpAmount={2.7}
        />
      </div>
      <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-10 px-4 md:grid-cols-2 md:px-6">
        <motion.div variants={stagger} initial="hidden" animate="show" className="max-w-2xl">
          <motion.p
            variants={fadeUp}
            className="mb-4 inline-flex rounded-full border border-white/20 bg-white/10 px-3.5 py-1.5 text-xs font-semibold tracking-wide text-blue-100 backdrop-blur-sm md:text-sm"
          >
            ALL-IN-ONE HEALTHCARE PLATFORM
          </motion.p>

          <motion.h1
            variants={fadeUp}
            className="mb-5 text-display font-bold text-white md:mb-6"
          >
            <span className="inline-flex items-center gap-3 whitespace-nowrap text-slate-200">
              <span>Go From</span>
              <motion.span
                animate={{
                  y: [0, -4, 0],
                  scale: [1, 1.04, 1],
                  boxShadow: [
                    "0 4px 12px rgba(255, 221, 0, 0.28)",
                    "0 8px 22px rgba(255, 221, 0, 0.45)",
                    "0 4px 12px rgba(255, 221, 0, 0.28)",
                  ],
                }}
                transition={{
                  duration: 2.8,
                  ease: "easeInOut",
                  repeat: Infinity,
                }}
                className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/15 text-white backdrop-blur-sm md:h-14 md:w-14"
              >
                <FileText className="h-7 w-7" />
              </motion.span>
              <span>Paper to</span>
            </span>
            <span className="block text-slate-100">
              Paperless with our Smart Healthcare Operations
            </span>
            <span className="block text-primary">
              in One Unified Platform
            </span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            className="type-measure mb-7 text-base leading-[1.65] text-slate-200 md:text-lead md:leading-[1.6]"
          >
            A complete Hospital Information System (HIS) to streamline workflows,
            improve patient care, and digitize your operations — trusted by
            healthcare providers for over 18 years.
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-col gap-3 sm:flex-row">
            <a
              href="#demo-form"
              className={`${primaryCtaClass} px-6 py-3 font-semibold`}
            >
              Book a Demo
            </a>
            <a
              href="#product"
              className="inline-flex items-center justify-center rounded-xl border border-white/30 bg-white/10 px-6 py-3 font-semibold text-white backdrop-blur-sm transition hover:border-white/60 hover:bg-white/15"
            >
              Explore Platform
            </a>
          </motion.div>

          <motion.div
            variants={fadeUp}
            className="mt-5 flex flex-wrap items-center gap-2 text-xs font-medium text-slate-200 md:text-sm"
          >
            <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">
              18+ years in healthcare
            </span>
            <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">
              End-to-end HIS workflows
            </span>
            <span className="rounded-full border border-white/20 bg-white/10 px-3 py-1 backdrop-blur-sm">
              Security-first architecture
            </span>
          </motion.div>
        </motion.div>

        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="hidden md:flex md:flex-col md:items-end md:gap-4"
        >
          <motion.div
            variants={fadeUp}
            ref={rightPanelRef}
            style={
              prefersReducedMotion
                ? undefined
                : { y: parallaxY, rotate: parallaxRotate, transformOrigin: "50% 50%" }
            }
            className="w-full max-w-md"
          >
            <ElectricBorder color="#FFDD00" speed={1} chaos={0.14} borderRadius={24}>
              <div className="relative isolate overflow-hidden rounded-3xl border border-white/25 bg-gradient-to-br from-indigo-950/75 via-violet-900/55 to-cyan-950/70 p-5 text-white shadow-2xl shadow-cyan-500/10 backdrop-blur-md">
                <div
                  className="pointer-events-none absolute -right-8 -top-10 h-36 w-36 rounded-full bg-cyan-400/35 blur-3xl"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute -bottom-12 -left-6 h-40 w-40 rounded-full bg-fuchsia-500/30 blur-3xl"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute bottom-8 right-6 h-24 w-24 rounded-full bg-amber-400/25 blur-2xl"
                  aria-hidden
                />
                <div
                  className="pointer-events-none absolute inset-0 -z-10 bg-gradient-to-br from-cyan-500/15 via-violet-500/10 to-emerald-400/15"
                  aria-hidden
                />
                <p className="bg-gradient-to-r from-cyan-200 via-sky-100 to-fuchsia-200 bg-clip-text text-xs font-semibold uppercase tracking-[0.18em] text-transparent">
                  Clinical command view
                </p>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div className="rounded-2xl border border-cyan-300/35 bg-gradient-to-br from-cyan-500/25 to-sky-600/20 p-3 shadow-lg shadow-cyan-500/10">
                    <p className="text-xs font-medium text-cyan-100">Active Patients</p>
                    <p className="mt-1 text-xl font-bold text-cyan-50">
                      <CounterValue value={1842} useGrouping reduceMotion={prefersReducedMotion} />
                    </p>
                  </div>
                  <div className="rounded-2xl border border-emerald-300/35 bg-gradient-to-br from-emerald-500/25 to-teal-600/20 p-3 shadow-lg shadow-emerald-500/10">
                    <p className="text-xs font-medium text-emerald-100">Daily OP Visits</p>
                    <p className="mt-1 text-xl font-bold text-emerald-50">
                      <CounterValue value={326} reduceMotion={prefersReducedMotion} />
                    </p>
                  </div>
                  <div className="rounded-2xl border border-amber-300/35 bg-gradient-to-br from-amber-500/25 to-orange-600/20 p-3 shadow-lg shadow-amber-500/10">
                    <p className="text-xs font-medium text-amber-100">Bed Occupancy</p>
                    <p className="mt-1 text-xl font-bold text-amber-50">
                      <CounterValue value={87} suffix="%" reduceMotion={prefersReducedMotion} />
                    </p>
                  </div>
                  <div className="rounded-2xl border border-fuchsia-300/35 bg-gradient-to-br from-fuchsia-500/25 to-violet-600/20 p-3 shadow-lg shadow-fuchsia-500/10">
                    <p className="text-xs font-medium text-fuchsia-100">Claims Cleared</p>
                    <p className="mt-1 text-xl font-bold text-fuchsia-50">
                      <CounterValue
                        value={98.6}
                        decimals={1}
                        suffix="%"
                        reduceMotion={prefersReducedMotion}
                      />
                    </p>
                  </div>
                </div>
              </div>
            </ElectricBorder>
          </motion.div>

          <motion.div variants={fadeUp} className="grid w-full max-w-md grid-cols-3 gap-3">
            <div className="rounded-2xl border border-white/20 bg-white/12 p-3 text-white backdrop-blur-md">
              <ShieldCheck className="h-5 w-5 text-blue-200" />
              <p className="mt-2 text-xs font-semibold text-blue-100">Security</p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/12 p-3 text-white backdrop-blur-md">
              <Building2 className="h-5 w-5 text-blue-200" />
              <p className="mt-2 text-xs font-semibold text-blue-100">Multi-site</p>
            </div>
            <div className="rounded-2xl border border-white/20 bg-white/12 p-3 text-white backdrop-blur-md">
              <Activity className="h-5 w-5 text-blue-200" />
              <p className="mt-2 text-xs font-semibold text-blue-100">Live analytics</p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
